import { BackendChatEvent } from '../types/aiChat.types'

const parseFrame = (frame: string): BackendChatEvent | null => {
  // SSE allows multiple `data:` lines per event; join them. The `event:` line is
  // redundant here (the JSON payload carries its own `type`).
  const data = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /, ''))
    .join('\n')

  if (!data) return null

  try {
    return JSON.parse(data) as BackendChatEvent
  } catch {
    return null
  }
}

/**
 * Decode a backend ai-chat SSE byte stream into typed {@link BackendChatEvent}s.
 * Frames are separated by a blank line and may split across reads, so we buffer
 * until a `\n\n` boundary. Framework-agnostic — no AI SDK dependency.
 */
export async function* parseEventStream(
  stream: ReadableStream<Uint8Array>
): AsyncGenerator<BackendChatEvent> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      // Strip CR so `\r\n`-framed streams (e.g. rewritten by a proxy) parse the
      // same as the backend's native `\n` framing.
      buffer += decoder.decode(value, { stream: true }).replace(/\r/g, '')

      let boundary = buffer.indexOf('\n\n')
      while (boundary !== -1) {
        const event = parseFrame(buffer.slice(0, boundary))
        buffer = buffer.slice(boundary + 2)
        if (event) yield event
        boundary = buffer.indexOf('\n\n')
      }
    }

    // Flush any trailing frame not terminated by a blank line.
    buffer += decoder.decode()
    const trailing = parseFrame(buffer)
    if (trailing) yield trailing
  } finally {
    reader.releaseLock()
  }
}
